import * as THREE from 'three';
import { BRINK_RADIUS, MOOR_RADIUS, pensLayout, trailLayout, type BattleState, type CazaPt, type MapDef, type PensLayout, type TrailBranch, type TrailCrossing, type TrailLayout } from '@somnhold/sim';
import { Kit, place } from '../seasons/kit.ts';
import { lookPoint, looksRng, Precip, type OmenCtx } from '../omens/common.ts';
import { BOAR_HIP_Y, BOAR_LEGS, boarBody, boarLeg } from '../models/boar.ts';
import { grayWolf } from '../models/caza.ts';
import { facingToRotY, SCALE, WORLD_PER_HEX } from './coords.ts';
import { GlowLayer } from './glow.ts';
import { SkyDome, smoothTiles } from './snow.ts';
import type { TerrainBuild } from './terrain.ts';

/**
 * The Long Hunt's places (docs/sendas/barbaro.md, chapter 1), for the barbarian's path: the tip of the titan's tail by
 * night, in the rain. Wet mud and dark grass, dry-stone walls, the clan's fires; cold moonlight and lightning far off;
 * the only warm things the fires and their gleam in the puddles, the only red the clan's wool and the dragon's.
 *
 * - `cazaPens` (act 1, and the scene at the wall), `cazaNight` (the opening's cinematic: the Grey Wolf over the wall):
 *   the clan's winter pens. A ring of dry-stone wall, its gate open toward the camera, a lean-to at the far side over
 *   the herd, troughs and hay racks, fires on iron baskets in the mud, puddles gleaming. The herd of stone boars (their
 *   legs trotting as they move); a wolf with one in its jaws: a red line to the stone it makes for and a ring there that
 *   closes as it gets there. The Grey Wolf, big and pale, over the wall and out with its boar.
 * - `cazaMoor` (act 2): the tail's pastures, dark wet grass, stone walls fallen in, cairns, standing stones daubed red;
 *   streams across the tracks, puddles. The tracks: prints full of rainwater that catch the moon, faint from afar and
 *   crisp within three hexes, where the clues show: the pack's gap where it jumped the stream, the Wolf's prints on the
 *   stones, the ring of prints by a puddle where the pack drank, the Wolf's pit and drag mark (each with a pale mark in
 *   the mud). Solved, the Wolf's prints fill with light, a line of it in the mud behind; the pack's, found wrong, go red.
 * - `cazaBrink` (act 3, the test), `cazaEnd` (the close): the ledge over the sea, a shelf of wet slate and mud; the cliff
 *   to the west in layered rock with grass on its steps, the drop to the east cut out of the board, the sea far below,
 *   its swell and foam, the Wolf's black rock and the White Island far out; the loose stones at the cliff's foot; the
 *   rain slanting in off the sea. The howler rearing (a red ring that breathes) and howling (rings going out). In the
 *   close, a grey head swimming out to sea.
 * - `cazaFire` (the opening): the clan's council round a great fire on the tail at night, hides on frames, poles with
 *   horns and red wool. `cazaCave` (the campfire): a shepherds' cave, its fold of stakes, the rain at its mouth.
 *
 * Terrón is drawn here (the herd's body and legs, smaller, mossed), on the field and in the scenes.
 *
 * Draw calls: the dressing (one merged mesh), the boars (bodies and legs, instanced), the prints, the clue marks, the
 * stones and pits (instanced), the streams (one mesh), the sea, the drag marks, the Grey Wolf (one model), the rain,
 * the glow layer, the sky. No lights of its own: the board's are repainted (HOURS) and flashed for the lightning.
 */

export const CAZA_PLACES = new Set(['cazaPens', 'cazaNight', 'cazaMoor', 'cazaBrink', 'cazaEnd', 'cazaFire', 'cazaCave', 'cazaFeast']);

/** The scenes' boards' seeds (client game/cine.ts builds the same). */
export const CAZA_SCENE_SEED = 7311;

/** The close's ground: the ledge's last stretch, the drop past x = 2600 (no crossings: the tracks end at its edge). */
export const CAZA_END_EDGE = 2600;
const CAZA_END_R = 7;
const cazaEndLayout = (): TrailLayout => ({
  kind: 'brink',
  radius: CAZA_END_R,
  rocks: [],
  west: Array.from({ length: 2 * CAZA_END_R + 1 }, (_, i) => -5200 - (i % 3) * 200),
  east: Array.from({ length: 2 * CAZA_END_R + 1 }, (_, i) => CAZA_END_EDGE + ((i * 7) % 3) * 150),
  crossings: [],
  end: { x: CAZA_END_EDGE, y: 0 },
  start: { x: 0, y: 1500 },
  stones: [],
  puddles: [{ x: -1500, y: 1800 }, { x: 400, y: -2200 }],
});

const S = WORLD_PER_HEX;
const K = S / 1000;
const RED = new THREE.Color('#ff4f3a');
const RED_SOFT = new THREE.Color('#ff8a6e');
const FIRE = new THREE.Color('#ff9a5a');
const FIRE_CORE = new THREE.Color('#fff0e6');
const MOON = new THREE.Color('#c8d8ff');
const WATER = new THREE.Color('#dfe9ff');
const STONE = ['#6b7078', '#5f646c', '#757a80', '#555a62', '#7f8388'];
const CLIFF = ['#55514c', '#4c4945', '#5d5852', '#47443f'];
/** The dry-stone wall's stones: warm greys, lichen paler. */
const DRY = ['#77726a', '#6b665f', '#827c73', '#5f5b55', '#8c867c'];
/** The pens' gate (its middle on the board). */
const g0 = (L: PensLayout): THREE.Vector3 => new THREE.Vector3(L.gateAt.x * K, 0, L.gateAt.y * K);
const GRASS = ['#55654b', '#4c5b44', '#5e6e53'];
const REED = ['#5a6a4c', '#667555', '#4d5b42'];
const WOOD = ['#4a3d32', '#3e342b', '#55473a'];
const CLAN = '#a43a2c';
const BOAR_TINTS = ['#ffffff', '#e6ebf2', '#f2eee8', '#dfe5e2', '#eef2f6'].map((c) => new THREE.Color(c));
const TERRON = new THREE.Color('#c9d6c4');

type Hour = 'day' | 'dusk' | 'night' | 'dawn';

/** The light: a rainy night on the tail (cold moon behind cloud, readable), and inside the shepherds' cave. */
const HOURS: Record<'night' | 'storm' | 'inside', { bg: string; fog: [string, number, number]; hemi: [string, string, number]; key: [string, number]; rim: [string, number]; sky: [string, string, string] }> = {
  night: { bg: '#172030', fog: ['#1e2737', 24, 86], hemi: ['#aab8d6', '#2a2824', 2.3], key: ['#cdd8f4', 1.95], rim: ['#8aa4ff', 1.2], sky: ['#070a10', '#212b3c', '#8a9fcf'] },
  storm: { bg: '#182131', fog: ['#1f2838', 22, 80], hemi: ['#a6b5d4', '#28261f', 2.25], key: ['#c9d5f2', 1.9], rim: ['#8eaaff', 1.35], sky: ['#06080e', '#1f2838', '#8296c6'] },
  inside: { bg: '#07080b', fog: ['#0b0c10', 14, 44], hemi: ['#a5afc4', '#1d1713', 1.7], key: ['#c4cee4', 0.85], rim: ['#ff7a5a', 0.95], sky: ['#000000', '#000000', '#000000'] },
};

/** The sea far below the ledge: a slow dark swell, broken foam, white where it meets the cliff (west). */
const seaMat = (): THREE.ShaderMaterial =>
  new THREE.ShaderMaterial({
    uniforms: { time: { value: 0 }, flash: { value: 0 } },
    vertexShader: /* glsl */ `
      varying vec2 vXZ;
      void main() { vec4 w = modelMatrix * vec4(position, 1.0); vXZ = w.xz; gl_Position = projectionMatrix * viewMatrix * w; }`,
    fragmentShader: /* glsl */ `
      uniform float time; uniform float flash; varying vec2 vXZ;
      float h(vec2 p) { p = mod(p, 289.0); return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
      float n(vec2 p) { vec2 i = floor(p), f = fract(p); vec2 u = f * f * (3.0 - 2.0 * f);
        return mix(mix(h(i), h(i + vec2(1, 0)), u.x), mix(h(i + vec2(0, 1)), h(i + vec2(1, 1)), u.x), u.y); }
      void main() {
        float sw = n(vXZ * 0.14 + vec2(time * 0.06, time * 0.02)) * 0.6 + n(vXZ * 0.4 - vec2(time * 0.1, 0.0)) * 0.4;
        float foam = smoothstep(0.76, 0.84, n(vec2(vXZ.x * 0.5 - time * 0.14, vXZ.y * 1.5 + sw * 3.0))) * smoothstep(0.35, 0.6, sw);
        float shore = smoothstep(10.0, 0.0, vXZ.x - 8.0);
        vec3 deep = vec3(0.035, 0.06, 0.095);
        vec3 col = mix(deep, vec3(0.09, 0.14, 0.2), sw * 0.7);
        col = mix(col, vec3(0.72, 0.78, 0.86), max(foam * 0.35, shore * smoothstep(0.45, 0.72, sw) * 0.6));
        col += flash * vec3(0.25, 0.3, 0.4) * (0.4 + sw);
        gl_FragColor = vec4(col, 1.0);
      }`,
  });

/** A stream's water: dark, moving, the moon in streaks on it. */
const streamMat = (): THREE.ShaderMaterial =>
  new THREE.ShaderMaterial({
    uniforms: { time: { value: 0 }, flash: { value: 0 } },
    transparent: true,
    depthWrite: false,
    vertexShader: /* glsl */ `
      attribute float along; varying float vA; varying float vS; attribute float side;
      void main() { vA = along; vS = side; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
    fragmentShader: /* glsl */ `
      uniform float time; uniform float flash; varying float vA; varying float vS;
      void main() {
        float edge = smoothstep(1.0, 0.45, abs(vS));
        // Ripples carried downstream, broken (two waves out of step), a thin glint of the moon on them.
        float st = sin(vA * 9.0 - time * 2.6 + vS * 3.0 + sin(vA * 2.3) * 1.5) * sin(vA * 4.1 - time * 1.7 - vS * 2.0);
        float gl = smoothstep(0.82, 1.0, st) * edge * 0.55;
        vec3 col = mix(vec3(0.07, 0.09, 0.12), vec3(0.13, 0.17, 0.22), edge);
        col += gl * vec3(0.42, 0.48, 0.58) + flash * vec3(0.18, 0.22, 0.28);
        gl_FragColor = vec4(col, 0.9 * smoothstep(1.0, 0.7, abs(vS)));
      }`,
  });

/** A paw print, flat: a pad and four toes (one geometry, the instances' shared). Its length along +z. */
const pawGeo = (): THREE.BufferGeometry => {
  const parts: THREE.BufferGeometry[] = [];
  const pad = new THREE.CircleGeometry(0.05, 9).rotateX(-Math.PI / 2);
  pad.scale(1.1, 1, 0.95);
  parts.push(pad);
  for (const [x, z, r] of [[-0.045, 0.05, 0.022], [0.045, 0.05, 0.022], [-0.018, 0.075, 0.024], [0.018, 0.075, 0.024]] as const) {
    const t = new THREE.CircleGeometry(r, 7).rotateX(-Math.PI / 2);
    t.translate(x, 0, z);
    parts.push(t);
  }
  return mergeFlat(parts);
};

/** Merges plain position-only geometries into one (prints, marks: no normals needed). */
const mergeFlat = (parts: THREE.BufferGeometry[]): THREE.BufferGeometry => {
  const pos: number[] = [];
  for (const p of parts) {
    const g = p.index ? p.toNonIndexed() : p;
    const a = g.getAttribute('position') as THREE.BufferAttribute;
    for (let i = 0; i < a.count; i++) pos.push(a.getX(i), a.getY(i), a.getZ(i));
    if (g !== p) g.dispose();
    p.dispose();
  }
  const out = new THREE.BufferGeometry();
  out.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  return out;
};

/** A print on the board: where, which way, how big, and what it is (its crossing and branch; -1: decoration). */
interface Print {
  x: number;
  y: number;
  yaw: number;
  s: number;
  c: number;
  b: number;
  /** A clue's own (the ring by a puddle): only seen up close. */
  clue: boolean;
}

/** A clue's mark in the mud: a pale ring or arc where it lies (only within sight of the hero). */
interface ClueMark {
  x: number;
  y: number;
  c: number;
  b: number;
  kind: 'jump' | 'stones' | 'drink' | 'pit';
}

export class CazaLayer {
  readonly group = new THREE.Group();
  private mats: THREE.Material[] = [];
  private geos: THREE.BufferGeometry[] = [];
  private glow: GlowLayer;
  private dome: SkyDome | null = null;
  private terrain: TerrainBuild | null = null;
  private rain: Precip | null = null;
  private now = 0;
  private inside: boolean;
  private hour: 'night' | 'storm' | 'inside';
  private rnd = looksRng(0xca2a);
  /** Static glows: fires and torches (flickering), puddles' gleams. */
  private lights: { p: THREE.Vector3; c: THREE.Color; size: number; a: number; seed: number; flick: number }[] = [];
  private embers: THREE.Vector3[] = [];
  private nextEmber = 0;
  private flash = 0;
  private nextFlash = 3;
  private look = new THREE.Vector3();
  private m4 = new THREE.Matrix4();
  private q4 = new THREE.Quaternion();
  private v3 = new THREE.Vector3();
  private s3 = new THREE.Vector3();
  private e3 = new THREE.Euler();
  private c3 = new THREE.Color();
  private zero = new THREE.Matrix4().makeScale(0, 0, 0);
  // The herd and Terrón.
  private P: PensLayout | null = null;
  private bodies: THREE.InstancedMesh | null = null;
  private legs: THREE.InstancedMesh | null = null;
  private boarTrack = new Map<number, { px: number; py: number; cx: number; cy: number; dist: number }>();
  private dragLine: THREE.InstancedMesh | null = null;
  private dragRing: THREE.InstancedMesh | null = null;
  private gray: THREE.Object3D | null = null;
  private grayAt = new THREE.Vector3();
  private grayShow = false;
  private grayLeap = 0;
  // The tracks.
  private T: TrailLayout | null = null;
  private prints: Print[] = [];
  private printMesh: THREE.InstancedMesh | null = null;
  private printKey = '';
  private clueShown = new Set<number>();
  private marks: ClueMark[] = [];
  private markMesh: THREE.InstancedMesh | null = null;
  private pitMesh: THREE.InstancedMesh | null = null;
  private pits: { x: number; y: number; c: number; yaw: number }[] = [];
  private stream: THREE.ShaderMaterial | null = null;
  /** Each crossing's stream (its water and banks), shown with its crossing; null where it has none. */
  private streams: (THREE.Group | null)[] = [];
  private sea: THREE.ShaderMaterial | null = null;
  private stones: THREE.Mesh[] = [];
  private stoneFall: number[] = [];
  private lit: { p: THREE.Vector3; at: number; c: number }[] = [];
  private shownAt = -1;
  private howlRing = 0;
  private headAt: THREE.Vector3 | null = null;
  private head: THREE.Group | null = null;
  private dropX = new Map<number, number>();
  private ledgeR = BRINK_RADIUS;
  private last: BattleState | null = null;

  constructor(
    private ctx: OmenCtx,
    private map: MapDef,
    readonly place: string,
    s: BattleState,
  ) {
    this.group.name = `caza-${place}`;
    this.inside = place === 'cazaCave';
    this.hour = this.inside ? 'inside' : place === 'cazaBrink' || place === 'cazaEnd' ? 'storm' : 'night';
    this.glow = new GlowLayer(ctx.low ? 220 : 420);
    const kit = new Kit();
    const seed = s.rules?.senda ? s.seed : CAZA_SCENE_SEED;
    if (place === 'cazaPens' || place === 'cazaNight') this.buildPens(kit, seed);
    else if (place === 'cazaMoor') this.buildTrail(kit, trailLayout(seed, 'tracks'));
    else if (place === 'cazaBrink') this.buildTrail(kit, trailLayout(seed, 'brink'));
    else if (place === 'cazaEnd') this.buildTrail(kit, cazaEndLayout());
    // (The tail by night after the Hunt, the camp up again round a great fire, the sky clear: chapter 5's epilogue.)
    else if (place === 'cazaFire' || place === 'cazaFeast') this.buildFire(kit);
    else this.buildCave(kit);
    this.buildBoars();
    const mat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.86, metalness: 0.04, flatShading: true });
    this.mats.push(mat);
    const dress = kit.build(mat, 'caza-dress');
    if (dress) {
      dress.castShadow = true;
      dress.receiveShadow = true;
      this.geos.push(dress.geometry);
      this.group.add(dress);
    }
    // The rain: slanting in off the sea on the ledge; at the cave's mouth only, past the overhang.
    if (!ctx.crypt && place !== 'cazaFeast') {
      const brink = place === 'cazaBrink' || place === 'cazaEnd';
      this.rain = new Precip({ count: ctx.low ? 700 : 1800, box: [16, 11, 16], fall: 15, wind: brink ? [-3.2, 0.6] : [-1.1, 0.5], size: [0.016, 0.5], color: '#dbe5f4', opacity: this.inside ? 0.3 : 0.46 }, 77);
      this.group.add(this.rain.mesh);
    }
    this.group.add(this.glow.mesh);
  }

  /* ---------- helpers ---------- */

  private track<T extends THREE.Material | THREE.BufferGeometry>(x: T): T {
    if (x instanceof THREE.Material) this.mats.push(x);
    else this.geos.push(x);
    return x;
  }

  private light(p: THREE.Vector3, color: THREE.Color, size: number, a: number, flick = 0): void {
    this.lights.push({ p: p.clone(), c: color, size, a, seed: this.lights.length * 1.37, flick });
  }

  /** A fire in an iron basket on a post (the pens' watch) or on the ground: logs, stones, its flames and embers. */
  private fire(kit: Kit, p: THREE.Vector3, basket: boolean, big = 1): void {
    const box = new THREE.BoxGeometry(1, 1, 1);
    const rock = new THREE.DodecahedronGeometry(0.5, 0);
    const y0 = basket ? 0.95 : 0;
    if (basket) {
      kit.add(box, '#2a2c30', place(p.x, p.y + 0.45, p.z, 0, new THREE.Vector3(0.07, 0.9, 0.07)));
      kit.add(new THREE.CylinderGeometry(0.26, 0.14, 0.24, 8, 1, true), '#2e3034', place(p.x, p.y + y0, p.z));
    } else {
      for (let i = 0; i < 9; i++) {
        const a = (i / 9) * Math.PI * 2;
        kit.add(rock, STONE[i % STONE.length] as string, place(p.x + Math.cos(a) * 0.42 * big, p.y + 0.06, p.z + Math.sin(a) * 0.42 * big, a, 0.2 * big));
      }
    }
    for (let i = 0; i < 4; i++) {
      const a = (i / 4) * Math.PI + 0.3;
      kit.add(box, WOOD[i % 3] as string, place(p.x, p.y + y0 + 0.06, p.z, a, new THREE.Vector3(0.08 * big, 0.08 * big, 0.55 * big), 0.25, 0));
    }
    kit.add(new THREE.CylinderGeometry(0.16 * big, 0.2 * big, 0.06, 8), '#ff7a3a', place(p.x, p.y + y0 + 0.05, p.z));
    box.dispose();
    rock.dispose();
    const f = new THREE.Vector3(p.x, p.y + y0 + 0.3 * big, p.z);
    this.light(f, FIRE_CORE, 0.9 * big, 0.95, 1);
    this.light(f.clone().setY(f.y + 0.2 * big), FIRE, 2.2 * big, 0.75, 1);
    this.light(f.clone().setY(p.y + 0.1), FIRE, 4.2 * big, 0.2, 0.5);
    this.embers.push(f);
  }

  /** A puddle in the mud: a sheet of water holding the sky's grey, a paler gleam, the moon or a fire's in it. */
  private puddle(kit: Kit, p: THREE.Vector3, r: number, warm: boolean): void {
    // (Water, not a stone: the first puddles, near black, read as dark flat stones. A rim of wet mud, the night sky's
    // blue-grey in the water, a pale streak of gleam and the rain's rings on it.)
    const disc = new THREE.CircleGeometry(1, 16);
    const ring = new THREE.RingGeometry(0.82, 1, 16);
    kit.add(disc, '#28241f', place(p.x, p.y + 0.022, p.z, r * 7, new THREE.Vector3(r * 1.18, r * 0.86, 1), -Math.PI / 2, 0));
    kit.add(disc, '#3b4652', place(p.x, p.y + 0.026, p.z, r * 7, new THREE.Vector3(r, r * 0.72, 1), -Math.PI / 2, 0));
    kit.add(disc, '#66778a', place(p.x + r * 0.15, p.y + 0.03, p.z - r * 0.1, r * 7 + 0.4, new THREE.Vector3(r * 0.5, r * 0.12, 1), -Math.PI / 2, 0));
    for (let i = 0; i < 3; i++) {
      const a = r * 11 + i * 2.1;
      const rr = r * (0.16 + i * 0.05);
      kit.add(ring, '#8d9cae', place(p.x + Math.cos(a) * r * 0.45, p.y + 0.032, p.z + Math.sin(a) * r * 0.32, 0, new THREE.Vector3(rr, rr, 1), -Math.PI / 2, 0));
    }
    disc.dispose();
    ring.dispose();
    this.light(new THREE.Vector3(p.x + r * 0.2, p.y + 0.05, p.z - r * 0.15), warm ? FIRE : MOON, r * 1.4, warm ? 0.32 : 0.22, warm ? 0.5 : 0);
  }

  /** A standing stone with the clan's red daub, or a cairn. */
  private marker(kit: Kit, p: THREE.Vector3, k: number): void {
    const box = new THREE.BoxGeometry(1, 1, 1);
    const rock = new THREE.DodecahedronGeometry(0.5, 0);
    if (k < 0.5) {
      kit.add(box, STONE[2] as string, place(p.x, p.y + 0.6, p.z, k * 9, new THREE.Vector3(0.36, 1.25, 0.24), 0.05, 0.06));
      kit.add(box, CLAN, place(p.x, p.y + 0.85, p.z, k * 9, new THREE.Vector3(0.37, 0.12, 0.245), 0.05, 0.06));
      kit.add(box, CLAN, place(p.x, p.y + 0.62, p.z, k * 9, new THREE.Vector3(0.37, 0.05, 0.245), 0.05, 0.06));
    } else for (let i = 0; i < 4; i++) kit.add(rock, STONE[(i + 1) % STONE.length] as string, place(p.x, p.y + 0.12 + i * 0.2, p.z, i * 1.3, 0.42 - i * 0.08));
    box.dispose();
    rock.dispose();
  }

  /** Grass in tufts over wet ground, a scatter of dark blades (avoiding a set of points). */
  private tufts(kit: Kit, n: number, R: number, ok: (x: number, z: number) => boolean): void {
    const cone = new THREE.ConeGeometry(0.06, 0.32, 3);
    const rnd = looksRng(0x7f7f);
    for (let i = 0; i < n; i++) {
      const x = (rnd() * 2 - 1) * R * S;
      const z = (rnd() * 2 - 1) * R * S * 0.88;
      if (!ok(x, z)) continue;
      const y = this.ctx.ground(x, z);
      for (let k = 0; k < 3; k++) kit.add(cone, GRASS[(i + k) % 3] as string, place(x + (k - 1) * 0.05, y + 0.13, z + (k % 2) * 0.04, i, new THREE.Vector3(1, 0.8 + rnd() * 0.6, 1), (k - 1) * 0.3, 0.15));
    }
    cone.dispose();
  }

  /* ---------- the pens ---------- */

  private buildPens(kit: Kit, seed: number): void {
    const L = pensLayout(seed);
    this.P = L;
    const rock = new THREE.DodecahedronGeometry(0.5, 0);
    const box = new THREE.BoxGeometry(1, 1, 1);
    // The dry-stone wall: a ring of rough stones piled without mortar, waist high, round the corral's middle (its
    // radius the wall's ring), unbroken but at the gate: three courses, the big ones below, a row of small ones on top,
    // a few fallen at its foot; lichen pale on some.
    const W = 6 * 866 * K;
    const gateA = Math.atan2(g0(L).x, g0(L).z);
    const rr = looksRng(seed ^ 0x5a11);
    const n = Math.round((Math.PI * 2 * W) / 0.26);
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2;
      const da = Math.atan2(Math.sin(a - gateA), Math.cos(a - gateA));
      if (Math.abs(da) < 0.17) continue;
      const rad = W + (rr() - 0.5) * 0.12;
      const x = Math.sin(a) * rad;
      const z = Math.cos(a) * rad;
      const y = this.ctx.ground(x, z);
      const c = DRY[Math.floor(rr() * DRY.length)] as string;
      kit.add(rock, c, place(x, y + 0.16, z, rr() * 6, new THREE.Vector3(0.36 + rr() * 0.1, 0.3, 0.32)));
      if (i % 2 === 0) kit.add(rock, DRY[(i + 1) % DRY.length] as string, place(x + (rr() - 0.5) * 0.06, y + 0.4, z, rr() * 6, new THREE.Vector3(0.32, 0.24, 0.3)));
      else kit.add(rock, DRY[(i + 3) % DRY.length] as string, place(x, y + 0.38, z, rr() * 6, new THREE.Vector3(0.3, 0.22, 0.3)));
      kit.add(rock, rr() < 0.15 ? '#9a9c92' : (DRY[(i + 2) % DRY.length] as string), place(x, y + 0.58, z, rr() * 6, new THREE.Vector3(0.22, 0.15, 0.22)));
      if (rr() < 0.07) kit.add(rock, c, place(x + Math.sin(a) * 0.35, y + 0.08, z + Math.cos(a) * 0.35, rr() * 6, 0.18));
    }
    // The gate: two posts and a gate of rails swung open inward.
    const g = this.ctx.at(L.gateAt.x, L.gateAt.y, 0);
    for (const dx of [-0.9, 0.9]) kit.add(box, WOOD[1] as string, place(g.x + dx * S, g.y + 0.6, g.z, 0, new THREE.Vector3(0.16, 1.2, 0.16)));
    for (const y of [0.3, 0.6, 0.9]) kit.add(box, WOOD[(y * 10) % 3] as string, place(g.x - 0.9 * S + 0.25, g.y + y, g.z - 0.6, 1.25, new THREE.Vector3(0.06, 0.08, 1.15)));
    kit.add(box, WOOD[2] as string, place(g.x, g.y + 1.25, g.z, 0, new THREE.Vector3(2.2 * S, 0.12, 0.14)));
    // The clan's horn hung over the gate on a cord.
    kit.add(new THREE.ConeGeometry(0.06, 0.32, 7), '#d8d5cf', place(g.x + 0.4, g.y + 1.05, g.z + 0.05, 0, 1, 0, 1.9));
    // The lean-to against the far wall behind the herd: posts, a roof of turf over boards sloping back to the wall.
    const c = { x: L.homes[0]!.x, y: L.homes[0]!.y };
    const roof = this.ctx.at(c.x, c.y - 1500, 0);
    for (const dx of [-2.2, 0, 2.2]) kit.add(box, WOOD[0] as string, place(roof.x + dx, roof.y + 0.65, roof.z + 0.55, 0, new THREE.Vector3(0.13, 1.3, 0.13)));
    kit.add(box, WOOD[2] as string, place(roof.x, roof.y + 1.28, roof.z + 0.55, 0, new THREE.Vector3(4.8, 0.12, 0.14)));
    kit.add(box, '#4b4038', place(roof.x, roof.y + 1.12, roof.z - 0.15, 0, new THREE.Vector3(5.0, 0.1, 1.5), -0.38, 0));
    kit.add(box, '#45503f', place(roof.x, roof.y + 1.19, roof.z - 0.17, 0, new THREE.Vector3(4.9, 0.05, 1.4), -0.38, 0));
    for (let i = 0; i < 9; i++) kit.add(box, '#5a5044', place(roof.x - 2.2 + i * 0.55, roof.y + 1.2, roof.z + 0.5, 0, new THREE.Vector3(0.04, 0.22, 0.04), 0.3, 0));
    // Troughs and hay racks (hay gone grey-green in the wet), a water butt.
    for (const [dx, dz] of [[-3.2, 1.6], [3.0, 1.2]] as const) {
      kit.add(box, WOOD[1] as string, place(roof.x + dx, roof.y + 0.18, roof.z + dz, 0.2, new THREE.Vector3(1.2, 0.3, 0.4)));
      kit.add(box, '#1e2633', place(roof.x + dx, roof.y + 0.32, roof.z + dz, 0.2, new THREE.Vector3(1.05, 0.03, 0.28)));
    }
    kit.add(box, WOOD[2] as string, place(roof.x - 1.5, roof.y + 0.5, roof.z - 0.8, 0, new THREE.Vector3(1.0, 0.9, 0.3)));
    kit.add(rock, '#59614c', place(roof.x - 1.5, roof.y + 0.95, roof.z - 0.8, 0, new THREE.Vector3(0.9, 0.3, 0.3)));
    kit.add(new THREE.CylinderGeometry(0.3, 0.3, 0.7, 10), WOOD[0] as string, place(roof.x + 2.0, roof.y + 0.35, roof.z - 0.6));
    // The fires: in iron baskets inside by the wall, on the ground outside; their gleam in the puddles.
    L.fires.forEach((f, i) => this.fire(kit, this.ctx.at(f.x, f.y, 0), i < 4, i < 4 ? 0.9 : 1.1));
    const rnd = looksRng(seed ^ 0x9e11);
    for (let i = 0; i < 16; i++) {
      const a = rnd() * Math.PI * 2;
      const d = (1 + rnd() * 4.5) * S;
      const p = this.ctx.at(Math.cos(a) * d / K, Math.sin(a) * d / K + 500, 0);
      this.puddle(kit, p, 0.18 + rnd() * 0.22, i % 3 === 0);
    }
    // Outside the wall: standing stones, a cart, hides on a frame, the tail's grass.
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2 + 0.4;
      this.marker(kit, this.ctx.at(Math.cos(a) * 8300, Math.sin(a) * 7600, 0), (i % 3) / 3);
    }
    this.tufts(kit, 900, 11, (x, z) => Math.hypot(x, z) > 7.4 * S);
    // Prints in the mud by the far wall (the scene at the wall; the fight's are its wolves').
    const pr: Print[] = [];
    for (let i = 0; i < 60; i++) {
      const a = -Math.PI / 2 + (rnd() - 0.5) * 1.4;
      const d = (4.4 + rnd() * 2.6) * 1000;
      pr.push({ x: Math.cos(a) * d, y: Math.sin(a) * d * 0.95, yaw: a + Math.PI / 2 + (rnd() - 0.5) * 0.8, s: i % 9 === 0 ? 1.7 : 1, c: -1, b: -1, clue: false });
    }
    // The boots going out: two lines of them past the wall (Ivar).
    for (let i = 0; i < 9; i++) pr.push({ x: -1600 + i * 120, y: -6800 - i * 380, yaw: Math.PI, s: 1.3, c: -1, b: -1, clue: false });
    this.prints = pr;
    this.buildPrints();
    // The drag marks: a red line from the dragging wolf to its stone and a ring there (instanced, eight at most).
    const strip = this.track(new THREE.PlaneGeometry(1, 1).rotateX(-Math.PI / 2).translate(0, 0, 0.5));
    const lm = this.track(new THREE.MeshBasicMaterial({ color: '#ffffff', transparent: true, opacity: 0.8, depthWrite: false, blending: THREE.AdditiveBlending }));
    this.dragLine = new THREE.InstancedMesh(strip, lm, 8);
    this.dragLine.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(24), 3);
    this.dragLine.count = 0;
    this.dragLine.frustumCulled = false;
    this.dragLine.renderOrder = 5;
    this.group.add(this.dragLine);
    const ring = this.track(new THREE.RingGeometry(0.78, 1, 32).rotateX(-Math.PI / 2));
    this.dragRing = new THREE.InstancedMesh(ring, lm, 16);
    this.dragRing.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(48), 3);
    this.dragRing.count = 0;
    this.dragRing.frustumCulled = false;
    this.dragRing.renderOrder = 5;
    this.group.add(this.dragRing);
    // The Grey Wolf: one model, shown while it is on the board.
    const gw = grayWolf({});
    gw.root.scale.setScalar(SCALE.enemy * 2.3);
    gw.root.visible = false;
    gw.root.traverse((o) => {
      const m = o as THREE.Mesh;
      if (m.isMesh) {
        m.castShadow = true;
        if (m.geometry) this.geos.push(m.geometry);
        const mm = m.material as THREE.Material | THREE.Material[];
        for (const x of Array.isArray(mm) ? mm : [mm]) this.mats.push(x);
      }
    });
    this.gray = gw.root;
    this.group.add(gw.root);
    rock.dispose();
    box.dispose();
  }

  /* ---------- the tracks ---------- */

  /** Prints along a branch: in `marks` lines side by side, left and right feet; a gap where the pack jumped a stream. */
  private branchPrints(c: number, b: number, br: TrailBranch, out: Print[]): void {
    const step = br.right ? 300 : 240;
    const gapAt = br.jump && br.stream >= 0 ? br.pts[br.stream]! : null;
    const lines = br.marks;
    let walked = 0;
    for (let i = 0; i + 1 < br.pts.length; i++) {
      const a = br.pts[i]!;
      const e = br.pts[i + 1]!;
      const dx = e.x - a.x;
      const dy = e.y - a.y;
      const d = Math.hypot(dx, dy);
      if (!d) continue;
      const yaw = Math.atan2(dx, dy);
      for (let t = (step - (walked % step)) % step; t < d; t += step) {
        const x = a.x + (dx * t) / d;
        const y = a.y + (dy * t) / d;
        const n = Math.round((walked + t) / step);
        if (gapAt && Math.hypot(x - gapAt.x, y - gapAt.y) < 650) continue;
        // (The first stretch at the crossing's middle is trodden by all: skip a little so they part clearly.)
        if (i === 0 && t < 250) continue;
        for (let l = 0; l < lines; l++) {
          const off = (l - (lines - 1) / 2) * 160 + (n % 2 ? 55 : -55);
          const ox = (dy / d) * off;
          const oy = (-dx / d) * off;
          out.push({ x: x + ox + ((l * 37 + n * 13) % 60) - 30, y: y + oy, yaw: yaw + (((n + l) % 3) - 1) * 0.12, s: br.right ? 1.45 : 1.0, c, b, clue: false });
        }
      }
      walked += d;
    }
    // The pack's stop at the puddle: prints round it.
    if (br.drink && br.puddle >= 0) {
      const p = br.pts[br.puddle]!;
      for (let k = 0; k < 10; k++) {
        const a = (k / 10) * Math.PI * 2;
        out.push({ x: p.x + 520 + Math.cos(a) * 520, y: p.y + Math.sin(a) * 480, yaw: a + Math.PI, s: 1, c, b, clue: true });
      }
    }
  }

  private buildTrail(kit: Kit, L: TrailLayout): void {
    this.T = L;
    const rock = new THREE.DodecahedronGeometry(0.5, 0);
    const box = new THREE.BoxGeometry(1, 1, 1);
    const brink = L.kind === 'brink';
    // Prints and clue marks for every crossing (shown crossing by crossing as the trail goes).
    const pr: Print[] = [];
    L.crossings.forEach((cr, c) => cr.branches.forEach((br, b) => this.branchPrints(c, b, br, pr)));
    this.prints = pr;
    for (let c = 0; c < L.crossings.length; c++) {
      const cr = L.crossings[c] as TrailCrossing;
      cr.branches.forEach((br, b) => {
        if (br.stream >= 0) this.marks.push({ ...br.pts[br.stream]!, c, b, kind: br.jump ? 'jump' : 'stones' });
        if (br.drink && br.puddle >= 0) this.marks.push({ x: br.pts[br.puddle]!.x + 520, y: br.pts[br.puddle]!.y, c, b, kind: 'drink' });
        if (br.pit >= 0) {
          const p = br.pts[br.pit]!;
          const q = br.pts[Math.max(0, br.pit - 1)]!;
          this.marks.push({ ...p, c, b, kind: 'pit' });
          this.pits.push({ x: p.x, y: p.y, c, yaw: Math.atan2(p.x - q.x, p.y - q.y) });
        }
        // Puddles the Wolf passed by without drinking.
        if (!br.drink && br.puddle >= 0) {
          const p = br.pts[br.puddle]!;
          this.puddle(kit, this.ctx.at(p.x + 560, p.y, 0), 0.32, false);
        }
        if (br.drink && br.puddle >= 0) {
          const p = br.pts[br.puddle]!;
          this.puddle(kit, this.ctx.at(p.x + 520, p.y, 0), 0.34, false);
        }
      });
    }
    this.buildPrints();
    this.buildClueMarks();
    // The streams: a ribbon of water from bank to bank, its stones where the Wolf crossed, reeds on its banks.
    this.buildStreams(kit, L);
    for (const p of L.puddles) this.puddle(kit, this.ctx.at(p.x, p.y, 0), 0.3 + ((p.x * 7) % 5) * 0.05, false);
    if (!brink) {
      // The pastures: fallen walls, cairns and standing stones, a shepherd's hut far off with its light; dark grass.
      const rnd = looksRng(0x3a11);
      for (let i = 0; i < 14; i++) {
        const a = rnd() * Math.PI * 2;
        const d = (MOOR_RADIUS - 1 + rnd() * 3) * 1000;
        this.marker(kit, this.ctx.at(Math.cos(a) * d, Math.sin(a) * d * 0.9, 0), rnd());
      }
      for (let w = 0; w < 5; w++) {
        const a = rnd() * Math.PI * 2;
        const d = (5 + rnd() * 5) * 1000;
        const yaw = rnd() * Math.PI;
        for (let i = 0; i < 6; i++) {
          if (rnd() < 0.3) continue;
          const x = Math.cos(a) * d + Math.cos(yaw) * (i - 3) * 380;
          const y = Math.sin(a) * d + Math.sin(yaw) * (i - 3) * 380;
          if (this.nearTrail(L, x, y, 900)) continue;
          const p = this.ctx.at(x, y, 0);
          kit.add(rock, STONE[i % STONE.length] as string, place(p.x, p.y + 0.15, p.z, i, new THREE.Vector3(0.4, 0.3, 0.34)));
          if (rnd() < 0.6) kit.add(rock, STONE[(i + 2) % STONE.length] as string, place(p.x, p.y + 0.4, p.z, i * 2, new THREE.Vector3(0.32, 0.24, 0.3)));
        }
      }
      const hut = this.ctx.at(-(MOOR_RADIUS + 3) * 1000, -6000, 0);
      kit.add(new THREE.CylinderGeometry(1.0, 1.1, 1.2, 9), STONE[3] as string, place(hut.x, hut.y + 0.6, hut.z));
      kit.add(new THREE.ConeGeometry(1.25, 1.0, 9), '#3a3a33', place(hut.x, hut.y + 1.7, hut.z));
      this.light(new THREE.Vector3(hut.x + 0.9, hut.y + 0.6, hut.z + 0.4), FIRE, 1.2, 0.6, 0.7);
      this.tufts(kit, 1400, MOOR_RADIUS + 2, (x, z) => !this.nearTrail(L, x / K, z / K, 700));
    } else this.buildLedge(kit, L);
    rock.dispose();
    box.dispose();
  }

  /** Whether a sim point is near any branch of the trail (keep the dressing off the prints). */
  private nearTrail(L: TrailLayout, x: number, y: number, r: number): boolean {
    for (const c of L.crossings) for (const b of c.branches) for (const p of b.pts) if (Math.abs(p.x - x) < r && Math.abs(p.y - y) < r) return true;
    return false;
  }

  /** The streams of the crossings: a ribbon of moving water, its banks, the stepping stones where the Wolf crossed. */
  private buildStreams(kit: Kit, L: TrailLayout): void {
    const rock = new THREE.DodecahedronGeometry(0.5, 0);
    const reed = new THREE.ConeGeometry(0.025, 0.45, 3);
    const m = this.track(streamMat());
    this.stream = m;
    for (const c of L.crossings) {
      this.streams.push(null);
      if (c.stream.length < 2) continue;
      const pos: number[] = [];
      const along: number[] = [];
      const side: number[] = [];
      const idx: number[] = [];
      // A smooth line through its points, a little wider than a hex.
      const pts: THREE.Vector3[] = c.stream.map((p) => this.ctx.at(p.x, p.y, 0.035));
      const curve = new THREE.CatmullRomCurve3(pts);
      const n = Math.max(8, pts.length * 10);
      const base = pos.length / 3;
      let run = 0;
      let prev: THREE.Vector3 | null = null;
      for (let i = 0; i <= n; i++) {
        const p = curve.getPoint(i / n);
        const tg = curve.getTangent(i / n);
        const nx = -tg.z;
        const nz = tg.x;
        const w = 0.32 + 0.08 * Math.sin(i * 0.9);
        if (prev) run += p.distanceTo(prev);
        prev = p;
        const y = Math.min(this.ctx.ground(p.x + nx * w, p.z + nz * w), this.ctx.ground(p.x - nx * w, p.z - nz * w)) + 0.04;
        pos.push(p.x + nx * w, y, p.z + nz * w, p.x - nx * w, y, p.z - nz * w);
        along.push(run, run);
        side.push(1, -1);
        if (i < n) {
          const a = base + i * 2;
          idx.push(a, a + 2, a + 1, a + 1, a + 2, a + 3);
        }
        // The banks: stones and reeds now and then.
      }
      const g = this.track(new THREE.BufferGeometry());
      g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
      g.setAttribute('along', new THREE.Float32BufferAttribute(along, 1));
      g.setAttribute('side', new THREE.Float32BufferAttribute(side, 1));
      g.setIndex(idx);
      // Its banks, stones and reeds, with it (shown with its crossing).
      const bank = new Kit();
      for (let i = 0; i <= n; i += 3) {
        const p = curve.getPoint(i / n);
        const tg = curve.getTangent(i / n);
        for (const k of [-1, 1]) {
          const bx = p.x - tg.z * 0.44 * k;
          const bz = p.z + tg.x * 0.44 * k;
          bank.add(rock, STONE[(i + (k > 0 ? 1 : 3)) % STONE.length] as string, place(bx, this.ctx.ground(bx, bz) + 0.03, bz, i, new THREE.Vector3(0.18, 0.08, 0.14)));
          if (i % 2 === 0) for (let r = 0; r < 3; r++) bank.add(reed, REED[r] as string, place(bx + r * 0.04, this.ctx.ground(bx, bz) + 0.2, bz + r * 0.03, r, 1, (r - 1) * 0.25, 0.2));
        }
      }
      // The Wolf's stepping stones: three flat pale stones across, where his branch crosses.
      const br = c.branches[c.right]!;
      if (br.stream >= 0) {
        const p = br.pts[br.stream]!;
        const q = br.pts[Math.min(br.pts.length - 1, br.stream + 1)]!;
        const dx = q.x - p.x;
        const dy = q.y - p.y;
        const d = Math.hypot(dx, dy) || 1;
        for (let k = -1; k <= 1; k++) {
          const sp = this.ctx.at(p.x + (dx / d) * k * 300, p.y + (dy / d) * k * 300, 0);
          bank.add(rock, '#8f949b', place(sp.x, sp.y + 0.05, sp.z, k * 1.7, new THREE.Vector3(0.34, 0.1, 0.28)));
        }
      }
      // The pack's: they jumped it; the banks there torn (darker mud).
      for (const b of c.branches) {
        if (!b.jump || b.stream < 0) continue;
        for (const k of [-1, 1]) {
          const q = b.pts[Math.max(0, Math.min(b.pts.length - 1, b.stream + k * 2))]!;
          const bp = this.ctx.at(q.x, q.y, 0);
          bank.add(new THREE.CircleGeometry(1, 10), '#241f1b', place(bp.x, bp.y + 0.03, bp.z, 0, new THREE.Vector3(0.22, 0.14, 1), -Math.PI / 2, 0));
        }
      }
      const grp = new THREE.Group();
      const water = new THREE.Mesh(g, m);
      water.renderOrder = 3;
      grp.add(water);
      const bm = this.track(new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.9, flatShading: true }));
      const banks = bank.build(bm, 'caza-banks');
      if (banks) {
        this.geos.push(banks.geometry);
        grp.add(banks);
      }
      grp.visible = false;
      grp.name = 'caza-stream';
      this.streams[this.streams.length - 1] = grp;
      this.group.add(grp);
    }
    rock.dispose();
    reed.dispose();
  }

  /** The ledge: the cliff in layered rock to the west, the drop to the east, the sea, the far rocks, the loose stones. */
  private buildLedge(kit: Kit, L: TrailLayout): void {
    const R = L.radius;
    this.ledgeR = R;
    const box = new THREE.BoxGeometry(1, 1, 1);
    const rock = new THREE.DodecahedronGeometry(0.5, 0);
    const cone = new THREE.ConeGeometry(0.06, 0.32, 3);
    for (let r = -R - 1; r <= R + 1; r++) {
      const i = Math.max(0, Math.min(2 * R, r + R));
      const w = (L.west[i] ?? -5600) * K;
      const e = (L.east[i] ?? 5600) * K;
      this.dropX.set(r, L.east[i] ?? 5600);
      const z = r * 866 * K;
      const k = Math.sin(r * 2.7) * 0.5 + 0.5;
      // The cliff (every other row: its slabs run two rows long): layered rock stepping up and back, each step's top
      // a ledge of turf, boulders fallen at its foot; warm dark greys, never a wall of cubes.
      const g = this.ctx.ground(w + 0.3, z);
      if ((r & 1) === 0) {
        for (let s = 0; s < 3; s++) {
          const h = 0.7 + s * 1.35 + k * 0.6;
          const x = w - 0.7 - s * 1.25 - k * 0.35;
          const yaw = (k - 0.5) * 0.35 + s * 0.05;
          // (Crags, not boxes: faceted rock bodies overlapping along the rows and up the steps. The first cliff of
          // boxes with a band on each read as terraces of blocks.)
          kit.add(rock, CLIFF[(s + (r & 3)) % CLIFF.length] as string, place(x, g + h * 0.42 - 0.25, z + 0.5 + (k - 0.5) * 0.6, yaw + s, new THREE.Vector3(2.6 + k * 0.6, h * 1.25, 2.0 * 0.866 * S * 1.45), (k - 0.5) * 0.2, (k - 0.5) * 0.25));
          kit.add(rock, '#363d31', place(x + 0.2, g + h - 0.18, z + 0.5, yaw + 1.3, new THREE.Vector3(1.2 + k * 0.3, 0.18, 1.3 * 0.866 * S)));
          // Its face broken: outcrops and fallen blocks along its front.
          for (let t = 0; t < 3; t++) kit.add(rock, CLIFF[(t + s) % CLIFF.length] as string, place(x + 0.8, g + h * (0.3 + t * 0.2) - 0.2, z + (t - 1) * 0.6 + 0.5, t * 2 + k * 5, new THREE.Vector3(0.6, h * 0.35, 0.55)));
          for (let t = 0; t < 4; t++) kit.add(cone, GRASS[t % 3] as string, place(x + 0.6 - t * 0.3, g + h - 0.1, z + 0.5 + (t - 1.5) * 0.4, t, 1.2, (t - 1) * 0.3, 0));
        }
        kit.add(rock, CLIFF[(r & 3) % CLIFF.length] as string, place(w - 0.2, g + 0.25, z + (k - 0.5), k * 6, new THREE.Vector3(0.7, 0.55, 0.6)));
        if (k > 0.5) kit.add(rock, CLIFF[2] as string, place(w + 0.15, g + 0.12, z - 0.4, k * 3, new THREE.Vector3(0.35, 0.28, 0.32)));
      }
      // The lip: stones along the edge; the face going down from it; the sea far below.
      const ge = this.ctx.ground(e - 0.4, z);
      kit.add(rock, STONE[(r & 3) + 1] as string, place(e - 0.25, ge + 0.06, z, k * 4, new THREE.Vector3(0.42, 0.2, 0.6)));
      // (The face down to the sea in rough rock bodies, overlapping, darker than the lip: a box a row stepped like stairs
      // along the edge.)
      kit.add(rock, CLIFF[(r & 3) % CLIFF.length] as string, place(e + 0.35 + k * 0.2, ge - 3.4, z + (k - 0.5) * 0.4, k * 5, new THREE.Vector3(1.4 + k * 0.5, 7.6, 0.866 * S * 1.6), (k - 0.5) * 0.15, 0));
      kit.add(rock, CLIFF[(r + 1) % CLIFF.length] as string, place(e + 1.1 + k * 0.7, ge - 10, z + 0.2, 0.1 + k, new THREE.Vector3(2.0, 7, 0.95 * S * 1.5)));
    }
    // The loose stones at the cliff's foot: round boulders on a crumbling step, a crack in the step under each.
    for (const p of L.stones) {
      const at = this.ctx.at(p.x, p.y, 0);
      kit.add(box, '#2c2a28', place(at.x, at.y + 0.02, at.z + 0.1, 0.3, new THREE.Vector3(0.9, 0.04, 0.05)));
      const geo = this.track(new THREE.DodecahedronGeometry(0.42, 1));
      const mat = this.track(new THREE.MeshStandardMaterial({ color: '#8d939b', roughness: 0.9, flatShading: true }));
      const m = new THREE.Mesh(geo, mat);
      m.position.set(at.x, at.y + 0.38, at.z);
      m.castShadow = true;
      this.stones.push(m);
      this.stoneFall.push(0);
      this.group.add(m);
    }
    // The sea and its foam; the Wolf's black rock far out, the White Island paler beyond it.
    const sea = seaMat();
    this.sea = sea;
    this.track(sea);
    const plane = new THREE.Mesh(this.track(new THREE.PlaneGeometry(300, 300).rotateX(-Math.PI / 2)), sea);
    plane.position.set(140, -16, 0);
    plane.name = 'caza-sea';
    this.group.add(plane);
    kit.add(rock, '#16181c', place(48, -14, -22, 0.4, new THREE.Vector3(7, 13, 6)));
    kit.add(rock, '#1c1f24', place(52, -15, -19, 1.1, new THREE.Vector3(4, 8, 4)));
    kit.add(rock, '#9aa2ac', place(78, -15.5, -40, 0.2, new THREE.Vector3(26, 4, 12)));
    for (let i = 0; i < 4; i++) kit.add(rock, STONE[4] as string, place(30 + i * 7, -15, 16 + i * 6, i, new THREE.Vector3(4 + i, 3 + i, 3)));
    // The grey head swimming out (the close).
    if (this.place === 'cazaEnd') {
      const head = new THREE.Group();
      const fur = this.track(new THREE.MeshStandardMaterial({ color: '#8c929b', roughness: 0.95, flatShading: true }));
      const h = new THREE.Mesh(this.track(new THREE.SphereGeometry(0.35, 10, 8)), fur);
      h.scale.set(1, 0.7, 1.3);
      head.add(h);
      for (const k of [-1, 1]) {
        const ear = new THREE.Mesh(this.track(new THREE.ConeGeometry(0.1, 0.3, 5)), fur);
        ear.position.set(k * 0.17, 0.25, -0.1);
        head.add(ear);
      }
      const eyeM = this.track(new THREE.MeshBasicMaterial({ color: '#ff4f3a' }));
      for (const k of [-1, 1]) {
        const eye = new THREE.Mesh(this.track(new THREE.SphereGeometry(0.04, 6, 5)), eyeM);
        eye.position.set(k * 0.12, 0.08, 0.36);
        head.add(eye);
      }
      const end = this.ctx.at(L.end.x, L.end.y, 0);
      this.headAt = new THREE.Vector3(end.x + 7, -15.7, end.z + 1);
      head.position.copy(this.headAt);
      this.head = head;
      this.group.add(head);
    }
    // Spray and sea mist off the edge, now and then (in the frame).
    box.dispose();
    rock.dispose();
    cone.dispose();
  }

  /* ---------- the scenes' places ---------- */

  private buildFire(kit: Kit): void {
    const box = new THREE.BoxGeometry(1, 1, 1);
    // Log benches round the fire, hides on frames behind, the clan's poles with horns and red wool.
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2 + 0.3;
      const p = this.ctx.at(Math.cos(a) * 2800, Math.sin(a) * 2500, 0);
      kit.add(new THREE.CylinderGeometry(0.16, 0.18, 1.4, 8), WOOD[i % 3] as string, place(p.x, p.y + 0.16, p.z, a, 1, 0, Math.PI / 2));
    }
    for (let i = 0; i < 4; i++) {
      const a = (i / 4) * Math.PI * 2 + 0.8;
      const p = this.ctx.at(Math.cos(a) * 4600, Math.sin(a) * 4200, 0);
      const yaw = Math.atan2(p.x, p.z);
      for (const dx of [-0.6, 0.6]) kit.add(box, WOOD[1] as string, place(p.x + Math.cos(yaw) * dx, p.y + 0.8, p.z - Math.sin(yaw) * dx, yaw, new THREE.Vector3(0.08, 1.6, 0.08)));
      kit.add(box, ['#5d5048', '#6c6058', '#4a423c', '#585048'][i] as string, place(p.x, p.y + 0.95, p.z, yaw, new THREE.Vector3(1.15, 1.1, 0.04)));
    }
    for (let i = 0; i < 5; i++) {
      const a = (i / 5) * Math.PI * 2;
      const p = this.ctx.at(Math.cos(a) * 3800, Math.sin(a) * 3400, 0);
      kit.add(box, WOOD[0] as string, place(p.x, p.y + 1.1, p.z, 0, new THREE.Vector3(0.1, 2.2, 0.1)));
      kit.add(new THREE.ConeGeometry(0.07, 0.42, 7), '#ddd9d1', place(p.x + 0.12, p.y + 2.15, p.z, 0, 1, 0, -0.9));
      kit.add(new THREE.ConeGeometry(0.07, 0.42, 7), '#ddd9d1', place(p.x - 0.12, p.y + 2.15, p.z, 0, 1, 0, 0.9));
      kit.add(box, CLAN, place(p.x, p.y + 1.75, p.z + 0.06, 0, new THREE.Vector3(0.05, 0.45, 0.02)));
    }
    this.fire(kit, this.ctx.at(0, 0, 0), false, 1.7);
    this.tufts(kit, 700, 9, (x, z) => Math.hypot(x, z) > 2.2 * S);
    const rnd = looksRng(0x1f1f);
    for (let i = 0; i < 6; i++) this.puddle(kit, this.ctx.at((rnd() - 0.5) * 9000, (rnd() - 0.5) * 8000, 0), 0.3, true);
    box.dispose();
  }

  private buildCave(kit: Kit): void {
    const rock = new THREE.DodecahedronGeometry(0.5, 0);
    const box = new THREE.BoxGeometry(1, 1, 1);
    // The cave: boulders all round but the mouth (south), their tops leaning in.
    for (let i = 0; i < 26; i++) {
      const a = (i / 26) * Math.PI * 2;
      if (Math.sin(a) > 0.55) continue;
      const d = 4600 + ((i * 37) % 7) * 120;
      const p = this.ctx.at(Math.cos(a) * d, Math.sin(a) * d * 0.85, 0);
      const h = 1.6 + ((i * 13) % 5) * 0.35;
      kit.add(rock, STONE[i % STONE.length] as string, place(p.x, p.y + h * 0.45, p.z, i, new THREE.Vector3(1.9, h, 1.6)));
    }
    // The fold of stakes at one side, bedding of old grey hay, Bera's oar against the rock, a pot by the fire.
    for (let i = 0; i < 7; i++) kit.add(box, WOOD[i % 3] as string, place(-3.2 + i * 0.32, 0.4, 1.6 - i * 0.18, 0.5, new THREE.Vector3(0.07, 0.8, 0.07)));
    kit.add(box, WOOD[1] as string, place(-2.2, 0.62, 1.05, 0.5, new THREE.Vector3(0.05, 0.05, 2.4)));
    kit.add(new THREE.CylinderGeometry(0.9, 1.0, 0.16, 10), '#5c6150', place(-1.6, 0.08, -1.0));
    kit.add(box, '#6a5747', place(2.6, 1.0, -2.6, 0.2, new THREE.Vector3(0.05, 2.2, 0.05), 0.0, -0.35));
    kit.add(box, '#6a5747', place(3.0, 2.0, -2.6, 0.2, new THREE.Vector3(0.14, 0.42, 0.03), 0.0, -0.35));
    kit.add(new THREE.CylinderGeometry(0.16, 0.12, 0.2, 8), '#2c2e32', place(0.55, 0.12, 0.35));
    this.fire(kit, this.ctx.at(0, 0, 0), false, 1.2);
    box.dispose();
    rock.dispose();
  }

  /* ---------- instanced pieces ---------- */

  private buildPrints(): void {
    if (!this.prints.length) return;
    const geo = this.track(pawGeo());
    const mat = this.track(new THREE.MeshBasicMaterial({ color: '#ffffff', transparent: true, opacity: 0.95, depthWrite: false, polygonOffset: true, polygonOffsetFactor: -3 }));
    const m = new THREE.InstancedMesh(geo, mat, this.prints.length);
    m.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(this.prints.length * 3), 3);
    m.frustumCulled = false;
    m.renderOrder = 4;
    m.name = 'caza-prints';
    this.printMesh = m;
    this.group.add(m);
    // The scenes' decoration shown at once; the trail's by its crossing (draw).
    this.prints.forEach((p, i) => {
      this.setPrint(i, p);
      m.setColorAt(i, this.c3.set('#4a5668'));
    });
    if (this.T) for (let i = 0; i < this.prints.length; i++) m.setMatrixAt(i, this.zero);
  }

  private setPrint(i: number, p: Print): void {
    const g = this.ctx.at(p.x, p.y, 0.02);
    this.q4.setFromAxisAngle(this.v3.set(0, 1, 0), p.yaw);
    this.m4.compose(g, this.q4, this.s3.set(p.s * 1.25, 1, p.s * 1.25));
    this.printMesh?.setMatrixAt(i, this.m4);
  }

  private buildClueMarks(): void {
    if (!this.marks.length) return;
    // A pale ring for each clue (an arc over a jump), lying in the mud; only within sight of the hero.
    const ring = this.track(new THREE.RingGeometry(0.82, 1, 28).rotateX(-Math.PI / 2));
    const mat = this.track(new THREE.MeshBasicMaterial({ color: '#ffffff', transparent: true, opacity: 0.85, depthWrite: false, blending: THREE.AdditiveBlending }));
    const m = new THREE.InstancedMesh(ring, mat, this.marks.length);
    m.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(this.marks.length * 3), 3);
    m.frustumCulled = false;
    m.renderOrder = 6;
    m.count = this.marks.length;
    for (let i = 0; i < this.marks.length; i++) m.setMatrixAt(i, this.zero);
    this.markMesh = m;
    this.group.add(m);
    if (this.pits.length) {
      // The Wolf's pit: a hollow where he set the boar down, and the drag mark after it.
      const parts = [new THREE.CircleGeometry(0.28, 12).rotateX(-Math.PI / 2), new THREE.PlaneGeometry(0.12, 0.7).rotateX(-Math.PI / 2).translate(0, 0, 0.42)];
      const pg = this.track(mergeFlat(parts));
      const pm = this.track(new THREE.MeshBasicMaterial({ color: '#141210', transparent: true, opacity: 0.9, depthWrite: false, polygonOffset: true, polygonOffsetFactor: -2 }));
      const pits = new THREE.InstancedMesh(pg, pm, this.pits.length);
      pits.frustumCulled = false;
      pits.renderOrder = 4;
      for (let i = 0; i < this.pits.length; i++) pits.setMatrixAt(i, this.zero);
      this.pitMesh = pits;
      this.group.add(pits);
    }
  }

  private buildBoars(): void {
    const mat = this.track(new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.88, flatShading: true }));
    const n = 14;
    this.bodies = new THREE.InstancedMesh(this.track(boarBody(3)), mat, n);
    this.bodies.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    for (let i = 0; i < n; i++) this.bodies.setColorAt(i, BOAR_TINTS[i % BOAR_TINTS.length] as THREE.Color);
    this.bodies.count = 0;
    this.bodies.frustumCulled = false;
    this.bodies.castShadow = true;
    this.group.add(this.bodies);
    this.legs = new THREE.InstancedMesh(this.track(boarLeg()), mat, n * 4);
    this.legs.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.legs.count = 0;
    this.legs.frustumCulled = false;
    this.group.add(this.legs);
  }

  /* ---------- the board, repainted ---------- */

  /**
   * Wet grass by night: two greens, bare mud where the herd and the rain wore it, and paler wet turf catching the moon.
   * (One green from edge to edge, the first pastures read as a flat dark sheet under the rain.)
   */
  private wetGrass(c: THREE.Color, x: number, z: number, k: number): void {
    const mud = Math.max(0, Math.sin(x * 0.23 + 2.1) * Math.sin(z * 0.19 - 0.7) - 0.35) * 2.2;
    const wet = Math.max(0, Math.sin(x * 0.61 - z * 0.47) * Math.cos(z * 0.53 + x * 0.12) - 0.4) * 1.8;
    c.set('#3b4834').lerp(this.c3.set('#28321f'), Math.max(0, Math.min(1, k)));
    c.lerp(this.c3.set('#59524a'), Math.min(1, mud));
    c.lerp(this.c3.set('#66765a'), Math.min(0.75, wet));
  }

  apply(terrain: TerrainBuild, _hour: Hour = 'night'): void {
    this.terrain = terrain;
    const c = new THREE.Color();
    const col = terrain.mesh.geometry.getAttribute('color') as THREE.BufferAttribute | undefined;
    const pos = terrain.mesh.geometry.getAttribute('position') as THREE.BufferAttribute | undefined;
    smoothTiles(terrain.mesh.geometry, terrain.heights, this.map.radius, terrain.ground);
    const pens = this.place === 'cazaPens' || this.place === 'cazaNight';
    if (col && pos) {
      terrain.mesh.updateMatrixWorld(true);
      const v = new THREE.Vector3();
      for (let i = 0; i < col.count; i++) {
        v.fromBufferAttribute(pos, i).applyMatrix4(terrain.mesh.matrixWorld);
        const lum = col.getX(i) * 0.3 + col.getY(i) * 0.59 + col.getZ(i) * 0.11;
        // Low and slow (no tile shows): two noises, a wide one and a finer one, blending two tones of the ground.
        const k = 0.5 + 0.25 * Math.sin(v.x * 0.37 + v.z * 0.21) * Math.cos(v.z * 0.31 - v.x * 0.17) + 0.25 * Math.sin(v.x * 0.9 + 1.3) * Math.sin(v.z * 0.8 + 0.4);
        const d = Math.hypot(v.x, v.z) / S;
        const mix2 = (a: string, b: string, t: number): THREE.Color => c.set(a).lerp(this.c3.set(b), Math.max(0, Math.min(1, t)));
        if (pens) {
          // Mud inside the wall (trodden darker by the gate and the herd), wet grass past it.
          if (d < 6.4) mix2('#4b433b', '#3b352f', k + (d < 2.5 ? 0.15 : 0));
          else this.wetGrass(c, v.x, v.z, k);
        } else if (this.place === 'cazaMoor' || this.place === 'cazaFire' || this.place === 'cazaFeast') this.wetGrass(c, v.x, v.z, k);
        else if (this.place === 'cazaCave') c.set(d < 4 ? '#3f3b36' : '#2b2b2c');
        // The ledge: wet slate, mud lying in its hollows.
        else mix2('#535860', '#4a4440', k * 0.9);
        c.multiplyScalar(0.94 + lum * 0.12);
        col.setXYZ(i, c.r, c.g, c.b);
      }
      col.needsUpdate = true;
    }
    terrain.decor.traverse((o) => {
      if (o.name === 'roads') o.visible = false;
      if (o.name === 'outskirts-ground') {
        if (this.inside) o.visible = false;
        else {
          const g = (o as THREE.Mesh).geometry.getAttribute('color') as THREE.BufferAttribute | undefined;
          if (g) {
            for (let i = 0; i < g.count; i++) {
              const lum = g.getX(i) * 0.3 + g.getY(i) * 0.59 + g.getZ(i) * 0.11;
              c.set(this.place === 'cazaBrink' || this.place === 'cazaEnd' ? '#3e434a' : (GRASS[i % 3] as string)).multiplyScalar(0.8 + lum * 0.35);
              g.setXYZ(i, c.r, c.g, c.b);
            }
            g.needsUpdate = true;
          }
        }
      }
      if ((o as THREE.InstancedMesh).isInstancedMesh) o.visible = false;
    });
    if (this.place === 'cazaBrink' || this.place === 'cazaEnd') this.carve(terrain);
    if (terrain.fog) terrain.fog.group.visible = !this.inside;
    if (!this.inside) {
      this.dome = new SkyDome();
      this.group.add(this.dome.mesh);
    }
    this.paint();
  }

  /** Past the ledge's edge (a world point): the drop. */
  private voidAt = (x: number, z: number): boolean => {
    if (!this.dropX.size) return false;
    const row = Math.round(z / K / 866);
    const edge = this.dropX.get(Math.max(-this.ledgeR - 1, Math.min(this.ledgeR + 1, row)));
    if (edge === undefined) return false;
    return x / K > edge + 300;
  };

  /** The drop cut out of the board's ground (and the ground beyond). */
  private carve(terrain: TerrainBuild): void {
    const cut = (mesh: THREE.Mesh): void => {
      const geo = mesh.geometry;
      const pos = geo.getAttribute('position') as THREE.BufferAttribute | undefined;
      if (!pos) return;
      mesh.updateMatrixWorld(true);
      const mw = mesh.matrixWorld;
      const v = new THREE.Vector3();
      const idx = geo.index;
      const tri = (i: number): number => (idx ? idx.getX(i) : i);
      const n = idx ? idx.count : pos.count;
      const keep: number[] = [];
      for (let i = 0; i < n; i += 3) {
        let cx = 0;
        let cz = 0;
        for (let k = 0; k < 3; k++) {
          v.fromBufferAttribute(pos, tri(i + k)).applyMatrix4(mw);
          cx += v.x;
          cz += v.z;
        }
        const out = this.voidAt(cx / 3, cz / 3);
        if (idx) {
          if (!out) keep.push(tri(i), tri(i + 1), tri(i + 2));
        } else if (out) {
          const x = pos.getX(i);
          const y = pos.getY(i);
          const z = pos.getZ(i);
          pos.setXYZ(i + 1, x, y, z);
          pos.setXYZ(i + 2, x, y, z);
        }
      }
      if (idx) geo.setIndex(keep);
      pos.needsUpdate = true;
      geo.computeBoundingSphere();
    };
    cut(terrain.mesh);
    terrain.decor.traverse((o) => {
      if (o.name === 'outskirts-ground') cut(o as THREE.Mesh);
    });
  }

  paint(_hour?: Hour): void {
    const look = HOURS[this.hour];
    const scene = this.ctx.scene;
    scene.background = new THREE.Color(look.bg);
    if (scene.fog instanceof THREE.Fog) {
      scene.fog.color.set(look.fog[0]);
      scene.fog.near = look.fog[1];
      scene.fog.far = look.fog[2];
    }
    const { hemi, key, rim } = this.ctx;
    if (hemi) {
      hemi.color.set(look.hemi[0]);
      hemi.groundColor.set(look.hemi[1]);
      hemi.intensity = look.hemi[2];
    }
    if (key) {
      key.color.set(look.key[0]);
      key.intensity = look.key[1];
    }
    if (rim) {
      rim.color.set(look.rim[0]);
      rim.intensity = look.rim[1];
    }
    this.dome?.set(look.sky[0], look.sky[1], look.sky[2]);
    if (this.dome) this.dome.u.uSunDir.value.set(-0.4, 0.35, -0.8);
    this.terrain?.fog?.tint({ low: look.fog[0], mid: look.fog[0], far: look.bg, alpha: 1 });
  }

  /* ---------- each tick and frame ---------- */

  tick(s: BattleState): void {
    this.last = s;
    for (const e of s._events ?? []) {
      if (e.t !== 'senda') continue;
      const p = this.ctx.at(e.x, e.y, 0.1);
      if (e.kind === 'band') {
        // Over the wall: a splash of mud where they land.
        this.ctx.vfx.burst(p, { n: 8, color: '#3a332c', speed: 0.04, up: 0.06, size: 0.12, life: 20, gravity: 0.006, spread: 0.4 });
      } else if (e.kind === 'grab') {
        this.ctx.vfx.ring(p, 0.2, 1.2, '#ff4f3a', 10, 0.8);
        this.ctx.vfx.burst(p, { n: 6, color: '#9aa0a8', speed: 0.03, up: 0.05, size: 0.08, life: 16, gravity: 0.006 });
      } else if (e.kind === 'saved') {
        this.ctx.flare(this.ctx.at(e.x, e.y, 0.5), '#e8f0ff', 2.2, 12);
        this.ctx.vfx.ring(p, 0.2, 1.3, '#e8f0ff', 12, 0.7);
      } else if (e.kind === 'taken' || e.kind === 'grayOut') {
        this.ctx.vfx.burst(p, { n: 12, color: '#6c727b', speed: 0.05, up: 0.06, size: 0.12, life: 22, gravity: 0.006, spread: 0.4 });
        this.ctx.vfx.ring(p, 0.3, 1.6, '#ff4f3a', 14, 0.6);
      } else if (e.kind === 'grayIn') {
        this.flash = 1;
        this.ctx.shake(0.2);
      } else if (e.kind === 'grayGrab') {
        this.ctx.flare(this.ctx.at(e.x, e.y, 0.6), '#ff4f3a', 2.4, 10);
        this.ctx.shake(0.15);
      } else if (e.kind === 'cross') {
        // The Wolf's prints fill with light, the crossing's middle to its end.
        const c = this.T?.crossings[e.n ?? 0];
        if (c) {
          const br = c.branches[c.right]!;
          br.pts.forEach((q, i) => this.lit.push({ p: this.ctx.at(q.x, q.y, 0.22), at: this.now + i * 0.07, c: e.n ?? 0 }));
          this.ctx.flare(this.ctx.at(br.pts.at(-1)!.x, br.pts.at(-1)!.y, 0.4), '#e8f0ff', 3, 16);
        }
      } else if (e.kind === 'wrong') {
        this.ctx.vfx.ring(p, 0.3, 2.0, '#ff4f3a', 16, 0.8);
        this.ctx.shake(0.15);
      } else if (e.kind === 'washed') {
        const c = this.T?.crossings[e.n ?? 0];
        if (c) for (const b of c.branches) for (let i = 0; i < b.pts.length; i += 3) this.ctx.vfx.burst(this.ctx.at(b.pts[i]!.x, b.pts[i]!.y, 0.1), { n: 2, color: '#8796ac', speed: 0.01, up: 0.02, size: 0.3, life: 30, smoke: true });
      } else if (e.kind === 'sniff') {
        this.ctx.vfx.ring(p, 0.2, 1.0, '#e8f0ff', 30, 0.5);
      } else if (e.kind === 'grunt') {
        this.shownAt = this.now;
      } else if (e.kind === 'rear') {
        this.ctx.vfx.ring(p, 0.3, 1.8, '#ff4f3a', 14, 0.9);
      } else if (e.kind === 'howl') {
        this.howlRing = 1;
      } else if (e.kind === 'cutHowl') {
        this.howlRing = 0;
        this.ctx.flare(this.ctx.at(e.x, e.y, 0.8), '#ffffff', 2.6, 12);
        this.ctx.shake(0.25);
      } else if (e.kind === 'howled') {
        this.howlRing = 0;
        this.ctx.vfx.ring(p, 0.5, 9, '#ff8a6e', 24, 0.6);
        this.ctx.shake(0.3);
      } else if (e.kind === 'rise') {
        this.ctx.vfx.burst(p, { n: 10, color: '#3a3346', speed: 0.02, up: 0.04, size: 0.3, life: 26, smoke: true });
      } else if (e.kind === 'stonePush') {
        this.ctx.shake(0.12);
      } else if (e.kind === 'stoneHit') {
        this.ctx.shake(e.n ? 0.4 : 0.2);
        this.ctx.vfx.burst(p, { n: 10, color: '#8d939b', speed: 0.05, up: 0.06, size: 0.12, life: 20, gravity: 0.008 });
        if (e.n) this.ctx.flare(this.ctx.at(e.x, e.y, 0.8), '#ffffff', 2.4, 10);
      } else if (e.kind === 'trailEnd') {
        this.ctx.flare(p, '#e8f0ff', 4, 20);
      }
    }
    // The boars' tracks (for their stride between ticks).
    const ws = s.senda?.pens;
    if (ws)
      ws.boars.forEach((b, i) => {
        const t = this.boarTrack.get(i);
        if (!t) this.boarTrack.set(i, { px: b.x, py: b.y, cx: b.x, cy: b.y, dist: 0 });
        else {
          t.dist += Math.hypot(b.x - t.cx, b.y - t.cy) * K;
          t.px = t.cx;
          t.py = t.cy;
          t.cx = b.x;
          t.cy = b.y;
        }
      });
    const t = s.senda?.npcs.find((n) => n.id === 'terron');
    if (t) {
      const k = this.boarTrack.get(99);
      if (!k) this.boarTrack.set(99, { px: t.x, py: t.y, cx: t.x, cy: t.y, dist: 0 });
      else {
        k.dist += Math.hypot(t.x - k.cx, t.y - k.cy) * K;
        k.px = k.cx;
        k.py = k.cy;
        k.cx = t.x;
        k.cy = t.y;
      }
    }
  }

  draw(s: BattleState): void {
    this.last = s;
    this.drawTrail(s);
    this.drawStones(s);
  }

  /** The trail's prints by its state: the crossing at hand's crisp within sight, faint beyond; the solved line lit. */
  private drawTrail(s: BattleState): void {
    const r = s.rules?.senda?.trail;
    const w = s.senda?.trail;
    const m = this.printMesh;
    if (!this.T || !m) return;
    const at = w?.at ?? -1;
    const washed = !!w && !!r && s.waveT >= (r.wash[at] ?? 1e12);
    const key = `${at}|${washed ? 1 : 0}|${w?.wrong.join('') ?? ''}`;
    const h = s.heroes.find((x) => x.downTicks === 0) ?? s.heroes[0];
    const see = r?.see ?? 3000;
    if (key !== this.printKey) {
      this.printKey = key;
      this.streams.forEach((g, i) => {
        if (g) g.visible = i === at;
      });
      this.prints.forEach((p, i) => {
        const show = p.c === at && !washed && !p.clue;
        if (p.clue) this.clueShown.delete(i);
        if (show || (p.c < at && this.T!.crossings[p.c]!.right === p.b)) this.setPrint(i, p);
        else m.setMatrixAt(i, this.zero);
      });
      m.instanceMatrix.needsUpdate = true;
    }
    // Colors: the crossing at hand's prints hold rainwater that catches the moon; crisp up close, faint far; the pack's
    // found wrong gone red; the solved line pale. A clue's ring of prints only up close.
    for (let i = 0; i < this.prints.length; i++) {
      const p = this.prints[i]!;
      if (p.c < at && this.T.crossings[p.c]!.right === p.b) {
        m.setColorAt(i, this.c3.set('#b9c8e4'));
        continue;
      }
      if (p.c !== at) continue;
      const d = h ? Math.hypot(p.x - h.x, p.y - h.y) : 1e9;
      const near = d < see;
      const k = near ? 1 : Math.max(0.28, 1 - (d - see) / 4000);
      const wrong = w?.wrong[p.b];
      // (The ring of prints by a puddle is the clue itself: there only up close.)
      if (p.clue) {
        if (near !== this.clueShown.has(i)) {
          if (near) {
            this.setPrint(i, p);
            this.clueShown.add(i);
          } else {
            m.setMatrixAt(i, this.zero);
            this.clueShown.delete(i);
          }
          m.instanceMatrix.needsUpdate = true;
        }
      }
      if (wrong) this.c3.set('#7a2a22').multiplyScalar(0.6 + 0.4 * k);
      else this.c3.set(near ? '#c9d6ee' : '#6c7a92').multiplyScalar(k);
      m.setColorAt(i, this.c3);
    }
    if (m.instanceColor) m.instanceColor.needsUpdate = true;
    // The clues' marks, and the pits, within sight only.
    const mm = this.markMesh;
    if (mm && h) {
      this.marks.forEach((c, i) => {
        const near = c.c === at && !washed && Math.hypot(c.x - h.x, c.y - h.y) < see;
        if (!near) {
          mm.setMatrixAt(i, this.zero);
          return;
        }
        const g = this.ctx.at(c.x, c.y, 0.05);
        const size = c.kind === 'drink' ? 0.95 : c.kind === 'pit' ? 0.5 : 0.6;
        this.m4.compose(g, this.q4.identity(), this.s3.set(size, 1, size));
        mm.setMatrixAt(i, this.m4);
        // The pack's (jump, drink) red-soft; the Wolf's (stones, pit) white.
        mm.setColorAt(i, this.c3.copy(c.kind === 'jump' || c.kind === 'drink' ? RED_SOFT : WATER).multiplyScalar(0.55 + 0.25 * Math.sin(this.now * 3 + i)));
      });
      mm.instanceMatrix.needsUpdate = true;
      if (mm.instanceColor) mm.instanceColor.needsUpdate = true;
    }
    const pm = this.pitMesh;
    if (pm && h) {
      this.pits.forEach((p, i) => {
        const near = p.c === at && !washed && Math.hypot(p.x - h.x, p.y - h.y) < see;
        if (!near) {
          pm.setMatrixAt(i, this.zero);
          return;
        }
        this.q4.setFromAxisAngle(this.v3.set(0, 1, 0), p.yaw);
        this.m4.compose(this.ctx.at(p.x, p.y, 0.03), this.q4, this.s3.set(1, 1, 1));
        pm.setMatrixAt(i, this.m4);
      });
      pm.instanceMatrix.needsUpdate = true;
    }
  }

  private drawStones(s: BattleState): void {
    const w = s.senda?.trail;
    if (!w || !this.stones.length) return;
    w.stones.forEach((st, i) => {
      const m = this.stones[i];
      if (!m) return;
      if (st.mode === 0) return;
      const p = this.ctx.at(st.x, st.y, 0);
      if (st.mode === 1) {
        m.position.set(p.x, p.y + 0.38, p.z);
        m.rotation.z -= 0.35;
      } else {
        // Over the edge: down to the sea.
        this.stoneFall[i] = (this.stoneFall[i] ?? 0) + 1;
        m.position.set(p.x + this.stoneFall[i]! * 0.08, p.y + 0.38 - this.stoneFall[i]! * this.stoneFall[i]! * 0.02, p.z);
        m.visible = m.position.y > -15;
      }
    });
  }

  /** The herd, Terrón and the Grey Wolf where they are between ticks; the drag marks. */
  private drawHerd(s: BattleState | null, alpha: number): void {
    const bodies = this.bodies;
    const legs = this.legs;
    if (!bodies || !legs) return;
    let n = 0;
    let nl = 0;
    const put = (x: number, y: number, facing: number, dist: number, moving: boolean, sc: number, tint: THREE.Color | null, lift: number, pitch: number, seed: number): void => {
      const g = this.ctx.at(x, y, lift);
      const stride = moving ? dist * 9.5 : 0;
      const bob = moving ? Math.abs(Math.sin(stride)) * 0.025 : Math.max(0, Math.sin(this.now * 0.9 + seed * 1.7)) * 0.01;
      const root = moving ? 0 : Math.max(0, Math.sin(this.now * 0.5 + seed * 2.3)) * 0.16;
      const yaw = facingToRotY(facing);
      this.e3.set(pitch + root, yaw, Math.sin(stride) * 0.03, 'YXZ');
      this.q4.setFromEuler(this.e3);
      this.m4.compose(this.v3.set(g.x, g.y + bob, g.z), this.q4, this.s3.set(sc, sc, sc));
      bodies.setMatrixAt(n, this.m4);
      if (tint) bodies.setColorAt(n, tint);
      for (let i = 0; i < 4; i++) {
        const [lx, lz] = BOAR_LEGS[i] as [number, number];
        const ph = i === 0 || i === 3 ? 1 : -1;
        this.e3.set(lift > 0.05 ? 0.7 * ph : Math.sin(stride) * 0.5 * ph, yaw, 0, 'YXZ');
        this.q4.setFromEuler(this.e3);
        const cy = Math.cos(yaw);
        const sy = Math.sin(yaw);
        this.m4.compose(this.v3.set(g.x + (lx * cy + lz * sy) * sc, g.y + BOAR_HIP_Y * sc + bob, g.z + (-lx * sy + lz * cy) * sc), this.q4, this.s3.set(sc, sc, sc));
        legs.setMatrixAt(nl++, this.m4);
      }
      n++;
    };
    const sc = SCALE.hero * 1.5;
    const ws = s?.senda?.pens;
    if (ws) {
      ws.boars.forEach((b, i) => {
        // Gone over the wall: not drawn; in the Grey Wolf's jaws: with it.
        if (b.lost && !(ws.gray.boar === i && ws.gray.phase >= 2 && ws.gray.phase < 4)) return;
        const t = this.boarTrack.get(i);
        const x = t ? t.px + (t.cx - t.px) * alpha : b.x;
        const y = t ? t.py + (t.cy - t.py) * alpha : b.y;
        const jaws = b.by !== 0;
        put(x, y, b.facing, t?.dist ?? 0, b.moving, sc, BOAR_TINTS[i % BOAR_TINTS.length] as THREE.Color, jaws ? 0.16 : 0, jaws ? -0.3 : 0, i);
      });
    } else if (this.P) {
      this.P.homes.forEach((h, i) => {
        if (this.place === 'cazaNight' && i === 3 && this.grayLeap > 0.45) return;
        put(h.x, h.y, 768 + ((i * 97) % 200) - 100, 0, false, sc, BOAR_TINTS[i % BOAR_TINTS.length] as THREE.Color, 0, 0, i);
      });
    }
    // Terrón: on the field (the path's person) or in a scene (a figure).
    const tn = s?.senda?.npcs.find((x) => x.id === 'terron' && x.hp > 0);
    if (tn) {
      const t = this.boarTrack.get(99);
      const x = t ? t.px + (t.cx - t.px) * alpha : tn.x;
      const y = t ? t.py + (t.cy - t.py) * alpha : tn.y;
      put(x, y, tn.facing, t?.dist ?? 0, tn.moving, sc * 0.8, TERRON, 0, 0, 13);
    } else {
      const fig = s?.heroes.find((h) => h.face === 'terron');
      if (fig) {
        const p = this.ctx.pos(fig.id);
        const lie = this.place === 'cazaCave';
        if (p) {
          const xx = p.x / K;
          const yy = p.z / K;
          put(xx, yy, fig.facing ?? 0, 0, false, sc * 0.8, TERRON, lie ? -0.1 : 0, 0, 13);
        }
      }
    }
    bodies.count = n;
    legs.count = nl;
    bodies.instanceMatrix.needsUpdate = true;
    legs.instanceMatrix.needsUpdate = true;
    if (bodies.instanceColor) bodies.instanceColor.needsUpdate = true;
    // The drag marks: from each wolf dragging a boar to its stone, a red line and a ring that closes as it nears.
    const dl = this.dragLine;
    const dr = this.dragRing;
    if (dl && dr) {
      let nd = 0;
      let nr = 0;
      for (const p of ws?.pack ?? []) {
        if (p.until <= 0 || nd >= 8) continue;
        const e = s?.enemies.find((x) => x.id === p.id);
        if (!e) continue;
        const a = this.ctx.pos(e.id) ?? this.ctx.at(e.x, e.y, 0);
        const b = this.ctx.at(p.exit.x, p.exit.y, 0.07);
        const left = Math.max(0, p.until - (s?.tick ?? 0)) / 36;
        const len = Math.hypot(b.x - a.x, b.z - a.z);
        this.q4.setFromAxisAngle(this.v3.set(0, 1, 0), Math.atan2(b.x - a.x, b.z - a.z));
        this.m4.compose(this.s3.set(a.x, this.ctx.ground(a.x, a.z) + 0.07, a.z), this.q4, new THREE.Vector3(0.09, 1, len));
        dl.setMatrixAt(nd, this.m4);
        dl.setColorAt(nd++, this.c3.copy(RED).multiplyScalar(0.6 + 0.4 * Math.sin(this.now * 12)));
        this.m4.compose(b, this.q4.identity(), this.s3.set(0.7 + 0.9 * left, 1, 0.7 + 0.9 * left));
        dr.setMatrixAt(nr, this.m4);
        dr.setColorAt(nr++, this.c3.copy(RED).multiplyScalar(0.9));
        this.m4.compose(b, this.q4, this.s3.set(0.7, 1, 0.7));
        dr.setMatrixAt(nr, this.m4);
        dr.setColorAt(nr++, this.c3.copy(RED_SOFT).multiplyScalar(0.7));
      }
      dl.count = nd;
      dr.count = nr;
      dl.instanceMatrix.needsUpdate = true;
      dr.instanceMatrix.needsUpdate = true;
      if (dl.instanceColor) dl.instanceColor.needsUpdate = true;
      if (dr.instanceColor) dr.instanceColor.needsUpdate = true;
    }
    // The Grey Wolf: over the wall in a leap, a run, and out with its boar.
    const gw = this.gray;
    if (gw) {
      const g = ws?.gray;
      if (g && g.phase >= 1 && g.phase < 4) {
        const at = this.ctx.at(g.x, g.y, 0);
        this.grayAt.lerp(at, this.grayShow ? 0.35 : 1);
        this.grayShow = true;
        const wall = this.P ? Math.abs(Math.hypot(g.x, g.y) - 6 * 866) : 9999;
        const leap = Math.max(0, 1 - wall / 1500) * 1.1;
        gw.position.set(this.grayAt.x, this.grayAt.y + leap + Math.abs(Math.sin(this.now * 9)) * 0.08, this.grayAt.z);
        gw.rotation.y = facingToRotY(g.facing);
        gw.visible = true;
      } else if (this.place === 'cazaNight' && this.P) {
        this.cineWolf(gw);
      } else gw.visible = false;
    }
  }

  /** The opening's Grey Wolf (no fight): over the wall's far side, to the herd, a boar in its jaws, out again. */
  private cineWolf(gw: THREE.Object3D): void {
    const P = this.P!;
    const loop = (this.now % 15.6) / 15.6;
    this.grayLeap = loop;
    const a = this.ctx.at(P.grayIn.x, P.grayIn.y - 1200, 0);
    const h = P.homes[3]!;
    const b = this.ctx.at(h.x, h.y, 0);
    const c = this.ctx.at(P.grayOut.x, P.grayOut.y - 1200, 0);
    let p: THREE.Vector3;
    let yaw: number;
    let up = 0;
    if (loop < 0.2) {
      p = a.clone();
      yaw = Math.atan2(b.x - a.x, b.z - a.z);
      gw.visible = loop > 0.05;
    } else if (loop < 0.45) {
      const k = (loop - 0.2) / 0.25;
      p = a.clone().lerp(b, k);
      up = Math.sin(Math.min(1, k * 2.2) * Math.PI) * 1.4;
      yaw = Math.atan2(b.x - a.x, b.z - a.z);
      gw.visible = true;
    } else if (loop < 0.55) {
      p = b.clone();
      yaw = Math.atan2(c.x - b.x, c.z - b.z);
      if (loop > 0.45 && loop < 0.47) this.flash = Math.max(this.flash, 0.8);
    } else if (loop < 0.8) {
      const k = (loop - 0.55) / 0.25;
      p = b.clone().lerp(c, k);
      up = Math.sin(Math.max(0, k * 2.2 - 1.2) * Math.PI) * 1.4;
      yaw = Math.atan2(c.x - b.x, c.z - b.z);
    } else {
      gw.visible = false;
      return;
    }
    gw.visible = true;
    gw.position.set(p.x, p.y + up + Math.abs(Math.sin(this.now * 9)) * 0.06, p.z);
    gw.rotation.y = yaw;
    // The boar in its jaws once taken.
    if (loop >= 0.45 && this.bodies) {
      const sc = SCALE.hero * 1.5;
      this.m4.compose(this.v3.set(p.x + Math.sin(yaw) * 0.9, p.y + up + 0.5, p.z + Math.cos(yaw) * 0.9), this.q4.setFromEuler(this.e3.set(-0.5, yaw + Math.PI / 2, 0.3)), this.s3.set(sc, sc, sc));
      this.bodies.setMatrixAt(this.bodies.count, this.m4);
      this.bodies.count++;
      this.bodies.instanceMatrix.needsUpdate = true;
    }
  }

  frame(now: number, camera?: THREE.Camera, alpha = 1): void {
    const dt = this.now ? Math.min(0.1, now - this.now) : 0;
    this.now = now;
    this.dome?.frame(now, camera);
    if (camera) lookPoint(camera, this.look);
    this.rain?.frame(now, this.look);
    if (this.sea) (this.sea.uniforms.time as THREE.IUniform<number>).value = now;
    if (this.stream) (this.stream.uniforms.time as THREE.IUniform<number>).value = now;
    // Lightning far off now and then: the whole board flashes cold for an instant (more often on the ledge).
    if (!this.inside && now > this.nextFlash) {
      this.nextFlash = now + (this.hour === 'storm' ? 5 : 8) + this.rnd() * 7;
      this.flash = 0.8 + this.rnd() * 0.2;
    }
    const look = HOURS[this.hour];
    const f = this.flash;
    if (this.ctx.key) this.ctx.key.intensity = look.key[1] + f * 3.2;
    if (this.ctx.hemi) this.ctx.hemi.intensity = look.hemi[2] + f * 1.4;
    if (this.sea) (this.sea.uniforms.flash as THREE.IUniform<number>).value = f;
    if (this.stream) (this.stream.uniforms.flash as THREE.IUniform<number>).value = f;
    this.flash = Math.max(0, this.flash - dt * (this.flash > 0.5 ? 6 : 2.5));
    this.drawHerd(this.last, alpha);
    // Embers off the fires, a few a second.
    if (this.embers.length && now > this.nextEmber) {
      this.nextEmber = now + (this.ctx.low ? 0.35 : 0.18);
      const e = this.embers[Math.floor(this.rnd() * this.embers.length)]!;
      this.ctx.vfx.burst(e, { n: 1, color: '#ff9a5a', speed: 0.006, up: 0.03, size: 0.05, life: 30, gravity: -0.0008, spread: 0.2 });
    }
    // The grey head out to sea (the close).
    if (this.head && this.headAt) {
      const k = (now % 30) / 30;
      this.head.position.set(this.headAt.x + k * 9, this.headAt.y + Math.sin(now * 2) * 0.04, this.headAt.z - k * 6);
      this.head.rotation.y = Math.atan2(9, -6);
      if (Math.floor(now * 3) % 2 === 0) this.ctx.vfx.ring(this.head.position.clone().setY(this.headAt.y - 0.15), 0.2, 0.9, '#c8d4e4', 14, 0.35);
    }
    this.glow.begin();
    for (const l of this.lights) {
      const fl = l.flick ? 0.84 + 0.16 * Math.sin(now * (6 + l.seed) + l.seed) * l.flick + 0.06 * Math.sin(now * 17 + l.seed * 3) * l.flick : 1;
      this.glow.add(l.p.x, l.p.y, l.p.z, l.c, l.size, l.a * fl);
    }
    // The solved line: each Wolf's print a bead of light, lit in turn as the crossing is solved, then low.
    for (const b of this.lit) {
      if (now < b.at) continue;
      const k = Math.max(0, 1 - (now - b.at) / 1.6);
      this.glow.add(b.p.x, b.p.y, b.p.z, WATER, 0.32 + k * 0.5, 0.22 + k * 0.5);
    }
    const s = this.last;
    // Terrón's grunt: a pulse of light down the Wolf's branch from the crossing.
    const w = s?.senda?.trail;
    const r = s?.rules?.senda?.trail;
    if (w && r && w.shown.includes(w.at)) {
      const br = r.crossings[w.at]?.branches[r.crossings[w.at]!.right];
      if (br)
        for (let i = 0; i < Math.min(br.pts.length, 8); i++) {
          const q = br.pts[i]!;
          const pulse = 0.5 + 0.5 * Math.sin(now * 5 - i * 0.8);
          this.glow.add(q.x * K, this.ctx.ground(q.x * K, q.y * K) + 0.25, q.y * K, WATER, 0.42, 0.25 + 0.45 * pulse);
        }
    }
    // The howler: rearing, a red ring that breathes faster; howling, pale rings going out.
    const st = s?.senda?.howler;
    if (st && (st.phase === 1 || st.phase === 2)) {
      const e = s?.enemies.find((x) => x.id === st.id);
      const p = e ? (this.ctx.pos(e.id) ?? this.ctx.at(e.x, e.y, 0)) : null;
      if (p) {
        const pulse = 0.5 + 0.5 * Math.sin(now * (st.phase === 1 ? 10 : 20));
        this.glow.add(p.x, p.y + 0.9, p.z, RED, 2.2 + pulse, 0.45 + 0.4 * pulse);
        if (st.phase === 2 && Math.floor(now * 4) !== Math.floor((now - dt) * 4)) this.ctx.vfx.ring(p.clone().setY(p.y + 0.5), 0.4, 3.4, '#e8eef8', 10, 0.55);
        if (st.phase === 1 && Math.floor(now * 3) !== Math.floor((now - dt) * 3)) this.ctx.vfx.ring(p.clone().setY(p.y + 0.06), 1.8, 0.5, '#ff4f3a', 10, 0.8);
      }
    }
    void this.howlRing;
    // Puddles' gleam flickers with the lightning.
    if (f > 0.1) this.glow.add(this.look.x, this.look.y + 8, this.look.z - 10, MOON, 30, f * 0.06);
    this.glow.end();
  }

  dispose(): void {
    for (const m of this.mats) m.dispose();
    for (const g of this.geos) g.dispose();
    this.dome?.dispose();
    this.rain?.dispose();
    this.glow.mesh.geometry.dispose();
    this.glow.material.dispose();
    this.group.removeFromParent();
  }
}

export type { CazaPt };
